import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import ForceGraph2D, { type ForceGraphMethods } from 'react-force-graph-2d'
import { controlCenterApi } from '@/lib/api'
import PageHeader from '@/components/PageHeader'

type Node = { id: string; label: string; type: string; group?: string; color?: string; val?: number }
type Link = { source: string | Node; target: string | Node; kind: string; color?: string }
type GraphData = { nodes: Node[]; links: Link[] }
type TopologyResponse = { success?: boolean; graph?: GraphData }

const TYPE_COLORS: Record<string, string> = {
  api: '#60a5fa',
  frontend: '#34d399',
  database: '#f59e0b',
  cache: '#a78bfa',
  external: '#9ca3af',
  service: '#94a3b8',
}

function inferType(node: Node): string {
  // services.yaml types are sometimes implicit — fall back to id heuristics
  if (TYPE_COLORS[node.type]) return node.type
  const id = node.id.toLowerCase()
  if (id.includes('postgres') || id.includes('db') || id.includes('mysql')) return 'database'
  if (id.includes('redis') || id.includes('cache')) return 'cache'
  return node.type || 'service'
}

function colorize(graph: GraphData): GraphData {
  return {
    nodes: graph.nodes.map((n) => {
      const t = inferType(n)
      return {
        ...n,
        type: t,
        color: TYPE_COLORS[t] ?? TYPE_COLORS.service,
        val: t === 'frontend' ? 14 : t === 'api' ? 12 : t === 'database' ? 16 : t === 'cache' ? 14 : 8,
      }
    }),
    links: graph.links,
  }
}

function getLinkEnd(end: string | Node): string {
  return typeof end === 'string' ? end : end.id
}

export default function Topology() {
  const { data, isLoading, isError, error } = useQuery<TopologyResponse>({
    queryKey: ['control-center', 'topology'],
    queryFn: controlCenterApi.getTopology,
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const fgRef = useRef<ForceGraphMethods | undefined>(undefined)
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 800, height: 600 })
  const [showDebug, setShowDebug] = useState(false)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set())

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const initialRect = el.getBoundingClientRect()
    if (initialRect.width > 0 && initialRect.height > 0) {
      setSize({ width: initialRect.width, height: initialRect.height })
    }
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect
      if (rect.width > 0 && rect.height > 0) {
        setSize({ width: rect.width, height: rect.height })
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const colorized = useMemo(
    () => (data?.graph ? colorize(data.graph) : { nodes: [], links: [] }),
    [data],
  )

  const filtered = useMemo(() => {
    if (hiddenTypes.size === 0) return colorized
    const visibleIds = new Set(colorized.nodes.filter((n) => !hiddenTypes.has(n.type)).map((n) => n.id))
    return {
      nodes: colorized.nodes.filter((n) => visibleIds.has(n.id)),
      links: colorized.links.filter(
        (l) => visibleIds.has(getLinkEnd(l.source)) && visibleIds.has(getLinkEnd(l.target)),
      ),
    }
  }, [colorized, hiddenTypes])

  // Configure forces once data is loaded — stronger repulsion + collision so clusters spread out
  useEffect(() => {
    if (!filtered.nodes.length || !fgRef.current) return
    const fg = fgRef.current as any
    fg.d3Force('charge')?.strength(-220)
    if (typeof fg.d3Force === 'function' && fg.d3Force('link')) {
      fg.d3Force('link').distance(80)
    }
    // collide force prevents node overlap
    const d3 = (window as any).d3 || null
    try {
      // dynamically import d3 collision via the force-graph internals
      const collide = fg.d3Force('collide')
      if (!collide && d3?.forceCollide) {
        fg.d3Force('collide', d3.forceCollide(20))
      } else if (collide && typeof collide.radius === 'function') {
        collide.radius(20)
      }
    } catch {
      // ignore — collision is a polish item, not load-bearing
    }
    fg.d3ReheatSimulation?.()
  }, [filtered])

  // Zoom to fit when data first arrives
  useEffect(() => {
    if (filtered.nodes.length > 0 && fgRef.current) {
      const t = setTimeout(() => {
        try {
          fgRef.current?.zoomToFit(400, 80)
        } catch {
          // ignore
        }
      }, 800)
      return () => clearTimeout(t)
    }
  }, [filtered])

  // Map of node id → set of neighbor ids (for hover highlighting)
  const neighbors = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const n of colorized.nodes) map.set(n.id, new Set([n.id]))
    for (const l of colorized.links) {
      const s = getLinkEnd(l.source)
      const t = getLinkEnd(l.target)
      map.get(s)?.add(t)
      map.get(t)?.add(s)
    }
    return map
  }, [colorized])

  const handleNodeClick = useCallback((node: any) => {
    const fg = fgRef.current as any
    if (!fg) return
    fg.centerAt?.(node.x, node.y, 600)
    fg.zoom?.(3, 600)
  }, [])

  const handleNodeHover = useCallback((node: any) => {
    setHoveredId(node?.id ?? null)
  }, [])

  // Counts per type for the legend
  const typeCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const n of colorized.nodes) counts.set(n.type, (counts.get(n.type) ?? 0) + 1)
    return counts
  }, [colorized])

  const hasData = filtered.nodes.length > 0
  const totalNodes = colorized.nodes.length
  const totalLinks = colorized.links.length

  return (
    <div className="p-8 space-y-4 h-full flex flex-col">
      <PageHeader
        title="Topology"
        description={`${filtered.nodes.length}/${totalNodes} services · ${filtered.links.length}/${totalLinks} dependencies · click a node to focus, hover to highlight neighbors`}
        actions={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fgRef.current?.zoomToFit(400, 80)}
              className="rounded-md border border-border bg-card px-3 py-1.5 text-xs"
            >
              Reset view
            </button>
            <button
              type="button"
              onClick={() => setShowDebug((v) => !v)}
              className="rounded-md border border-border bg-card px-3 py-1.5 text-xs"
            >
              {showDebug ? 'Hide' : 'Show'} raw data
            </button>
          </div>
        }
      />

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load topology: {(error as Error)?.message ?? 'unknown error'}
        </div>
      ) : null}

      {showDebug ? (
        <div className="rounded-md border border-border bg-card p-3 text-xs font-mono overflow-auto max-h-64">
          <pre>{JSON.stringify(data, null, 2)}</pre>
        </div>
      ) : null}

      <div
        ref={containerRef}
        className="flex-1 min-h-[500px] rounded-lg border border-border overflow-hidden relative"
        style={{ background: '#0b1120' }}
      >
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            Loading topology…
          </div>
        ) : !hasData ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-sm text-muted-foreground p-8 text-center">
            <p className="text-base font-medium text-foreground mb-2">No graph data to show</p>
            <p className="max-w-md">
              {colorized.nodes.length === 0
                ? 'Topology endpoint returned no services. Check authentication and that services.yaml has entries with depends: declared.'
                : 'All node types are filtered out. Re-enable a type below to see nodes.'}
            </p>
          </div>
        ) : (
          <ForceGraph2D
            ref={fgRef as any}
            width={size.width}
            height={size.height}
            graphData={filtered as any}
            nodeLabel={(n: any) => `${n.label} (${n.type})`}
            nodeVal={(n: any) => n.val}
            backgroundColor="#0b1120"
            linkDirectionalArrowLength={4}
            linkDirectionalArrowRelPos={1}
            linkWidth={(l: any) => {
              if (!hoveredId) return 1.5
              const s = getLinkEnd(l.source)
              const t = getLinkEnd(l.target)
              return s === hoveredId || t === hoveredId ? 3 : 1
            }}
            linkColor={(l: any) => {
              if (!hoveredId) return 'rgba(148, 163, 184, 0.5)'
              const s = getLinkEnd(l.source)
              const t = getLinkEnd(l.target)
              return s === hoveredId || t === hoveredId
                ? 'rgba(96, 165, 250, 0.9)'
                : 'rgba(148, 163, 184, 0.12)'
            }}
            nodeCanvasObject={(node: any, ctx, globalScale) => {
              const isHovered = node.id === hoveredId
              const isNeighbor = hoveredId ? neighbors.get(hoveredId)?.has(node.id) ?? false : true
              const dim = hoveredId && !isNeighbor
              const radius = Math.max(4, (node.val ?? 8) * 0.6)
              const baseColor = node.color ?? '#94a3b8'

              // node sphere
              ctx.beginPath()
              ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false)
              ctx.fillStyle = dim ? `${baseColor}33` : baseColor
              ctx.fill()
              ctx.strokeStyle = isHovered ? '#fff' : dim ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.4)'
              ctx.lineWidth = isHovered ? 2 : 1
              ctx.stroke()

              // label
              const fontSize = Math.max(9, 12 / globalScale)
              ctx.font = `${isHovered ? 'bold ' : ''}${fontSize}px ui-sans-serif, system-ui, -apple-system, sans-serif`
              ctx.textAlign = 'center'
              ctx.textBaseline = 'top'
              ctx.fillStyle = dim ? 'rgba(229, 231, 235, 0.25)' : '#e5e7eb'
              ctx.fillText(String(node.label ?? node.id), node.x, node.y + radius + 2)
            }}
            nodePointerAreaPaint={(node: any, color, ctx) => {
              const radius = Math.max(8, (node.val ?? 8) * 0.8)
              ctx.beginPath()
              ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false)
              ctx.fillStyle = color
              ctx.fill()
            }}
            onNodeClick={handleNodeClick}
            onNodeHover={handleNodeHover}
            warmupTicks={100}
            cooldownTicks={300}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted-foreground mr-1">Filter by type:</span>
        {Object.entries(TYPE_COLORS).map(([type, color]) => {
          const count = typeCounts.get(type) ?? 0
          const isHidden = hiddenTypes.has(type)
          return (
            <button
              key={type}
              type="button"
              onClick={() => {
                setHiddenTypes((prev) => {
                  const next = new Set(prev)
                  if (next.has(type)) next.delete(type)
                  else next.add(type)
                  return next
                })
              }}
              disabled={count === 0}
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition-opacity ${
                count === 0 ? 'opacity-30 cursor-not-allowed' : isHidden ? 'opacity-40' : ''
              }`}
              style={{ borderColor: `${color}66` }}
            >
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
              <span className="capitalize">{type}</span>
              <span className="text-muted-foreground">({count})</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
